import React, { useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { HomeButton } from "./MemoryPalAILearnMore";

import image1 from "../images/theFarmerAndTheThylacineDuo.png";
import image2 from "../images/TheFarmer_and_TheThylacine.png";
import image3 from "../images/PRLosDiablos.png";
import image4 from "../images/IslandOfTheHarvesterAliens.png";
import image5 from "../images/IslandOfTheHarvesterAliens2.png";
import image6 from "../images/the-plain-reckoning2.jpg";

// Set to false for a simpler stacked fallback
const USE_CINEMA_LAYOUT = true;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  position: relative;
  min-height: 100vh;
  padding-bottom: 7rem;
  color: #e8ffe9;
  font-family: "VT323", monospace;
  background: linear-gradient(
    to bottom,
    #000 0%,
    #000 calc(100% - 11rem),
    rgba(0, 0, 0, 0.72) calc(100% - 5.5rem),
    rgba(0, 0, 0, 0.28) calc(100% - 2rem),
    transparent 100%
  );
`;

const Body = styled.div`
  width: min(1180px, calc(100% - 2.5rem));
  margin: 0 auto;
  padding: 2.25rem 0 1.5rem;
`;

const Hero = styled.section`
  position: relative;
  min-height: min(88vh, 820px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  isolation: isolate;
`;

const HeroArt = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    transform: scale(1.02);
    transition: opacity 0.45s ease;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, rgba(0, 0, 0, 0.92) 0%, rgba(0, 0, 0, 0.55) 42%, rgba(0, 0, 0, 0.25) 70%, rgba(0, 0, 0, 0.55) 100%),
      linear-gradient(0deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.35) 45%, rgba(0, 0, 0, 0.45) 100%);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100% - 2.5rem));
  margin: 0 auto;
  padding: 7.5rem 0 2.75rem;
  animation: ${fadeIn} 0.65s ease both;

  @media (max-width: 700px) {
    padding: 6.5rem 0 2rem;
  }
`;

const NowShowing = styled.p`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #45fe47;
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  max-width: 14ch;
  font-size: clamp(2.6rem, 7vw, 4.6rem);
  font-weight: 400;
  line-height: 0.95;
  text-transform: uppercase;
  color: #fff;
  text-shadow: 0 0 24px rgba(0, 0, 0, 0.8);
`;

const HeroSubtitle = styled.p`
  margin: 0 0 1rem;
  max-width: 34rem;
  font-size: clamp(1.15rem, 2.4vw, 1.45rem);
  font-style: italic;
  color: rgba(232, 255, 233, 0.88);
`;

const HeroLogline = styled.p`
  margin: 0 0 1.25rem;
  max-width: 38rem;
  font-size: clamp(1.05rem, 2vw, 1.25rem);
  line-height: 1.45;
  color: rgba(69, 254, 71, 0.9);
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.35rem;
`;

const Tag = styled.span`
  padding: 0.28rem 0.7rem;
  border: 1px solid rgba(69, 254, 71, 0.7);
  color: #45fe47;
  font-size: 0.98rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const ActionBtn = styled.button`
  font-family: "VT323", monospace;
  font-size: 1.15rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.7rem 1.15rem;
  border: 1px solid #45fe47;
  cursor: pointer;
  background: ${(p) => (p.$primary ? "#45fe47" : "rgba(0, 0, 0, 0.45)")};
  color: ${(p) => (p.$primary ? "#000" : "#45fe47")};
  backdrop-filter: blur(6px);

  &:hover {
    background: #fff;
    color: #000;
    border-color: #fff;
  }
`;

const ReelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 1rem;
  margin-bottom: 1rem;
  animation: ${fadeIn} 0.55s ease 0.1s both;
`;

const ReelTitle = styled.h2`
  margin: 0;
  font-size: 1.55rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
`;

const ReelNote = styled.p`
  margin: 0;
  font-size: 1.05rem;
  color: #8b949e;
`;

const Reel = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(170px, 210px);
  gap: 0.9rem;
  overflow-x: auto;
  padding: 0.25rem 0.15rem 1.1rem;
  margin-bottom: 1.75rem;
  scroll-snap-type: x mandatory;
  animation: ${fadeIn} 0.55s ease 0.16s both;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #45fe47;
    border-radius: 8px;
  }

  @media (max-width: 600px) {
    grid-auto-columns: minmax(148px, 68vw);
  }
`;

const PosterCard = styled.button`
  position: relative;
  display: block;
  padding: 0;
  border: 1px solid ${(p) => (p.$active ? "#45fe47" : "rgba(69, 254, 71, 0.3)")};
  background: #050505;
  cursor: pointer;
  scroll-snap-align: start;
  text-align: left;
  overflow: hidden;
  transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${(p) =>
    p.$active ? "0 0 22px rgba(69, 254, 71, 0.22)" : "none"};

  &:hover {
    transform: translateY(-4px);
    border-color: #45fe47;
  }
`;

const PosterArt = styled.div`
  aspect-ratio: 2 / 3;
  background: #000;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const PosterCaption = styled.div`
  padding: 0.65rem 0.7rem 0.8rem;
  border-top: 1px solid rgba(69, 254, 71, 0.25);
`;

const PosterName = styled.p`
  margin: 0 0 0.2rem;
  font-size: 1.05rem;
  line-height: 1.15;
  color: #e8ffe9;
  text-transform: uppercase;
`;

const PosterMeta = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: #45fe47;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ActiveMark = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 0.15rem 0.45rem;
  background: #45fe47;
  color: #000;
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const DetailDock = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 1.25rem;
  padding: 1.15rem;
  border: 1px solid rgba(69, 254, 71, 0.45);
  background: rgba(0, 8, 0, 0.72);
  animation: ${fadeIn} 0.4s ease both;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGallery = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const DetailMainShot = styled.button`
  padding: 0;
  border: 1px solid rgba(69, 254, 71, 0.35);
  background: #000;
  cursor: pointer;
  aspect-ratio: 16 / 10;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DetailThumbs = styled.div`
  display: flex;
  gap: 0.45rem;
`;

const DetailThumb = styled.button`
  width: 64px;
  height: 48px;
  padding: 0;
  border: 1px solid ${(p) => (p.$active ? "#45fe47" : "rgba(69, 254, 71, 0.3)")};
  background: #000;
  cursor: pointer;
  opacity: ${(p) => (p.$active ? 1 : 0.6)};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DetailCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const DetailKicker = styled.p`
  margin: 0;
  font-size: 0.95rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #45fe47;
`;

const DetailTitle = styled.h3`
  margin: 0;
  font-size: clamp(1.55rem, 3vw, 2.1rem);
  font-weight: 400;
  line-height: 1.1;
  color: #fff;
  text-transform: uppercase;
`;

const DetailSubtitle = styled.p`
  margin: 0;
  font-size: 1.15rem;
  font-style: italic;
  color: rgba(232, 255, 233, 0.8);
`;

const DetailBody = styled.p`
  margin: 0;
  font-size: 1.12rem;
  line-height: 1.45;
  color: #cfcfcf;
  white-space: pre-line;
`;

const DetailEmpty = styled.p`
  margin: 0;
  font-size: 1.1rem;
  font-style: italic;
  color: #8b949e;
`;

const CtaBand = styled.section`
  margin-top: 2.5rem;
  padding: 1.6rem 1.2rem;
  border-top: 1px solid rgba(69, 254, 71, 0.35);
  border-bottom: 1px solid rgba(69, 254, 71, 0.35);
  text-align: center;
`;

const CtaTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-size: 1.55rem;
  font-weight: 400;
  text-transform: uppercase;
  color: #fff;
`;

const CtaText = styled.p`
  margin: 0 auto 1rem;
  max-width: 34rem;
  font-size: 1.1rem;
  line-height: 1.4;
  color: rgba(69, 254, 71, 0.85);
`;

const CtaActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const CtaLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 150px;
  padding: 0.65rem 1.1rem;
  font-family: "VT323", monospace;
  font-size: 1.1rem;
  text-decoration: none;
  text-transform: uppercase;
  border: 1px solid #45fe47;
  background: ${(p) => (p.$primary ? "#45fe47" : "transparent")};
  color: ${(p) => (p.$primary ? "#000" : "#45fe47")};

  &:hover {
    background: #fff;
    color: #000;
    border-color: #fff;
  }
`;

const CtaExternal = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 150px;
  padding: 0.65rem 1.1rem;
  font-family: "VT323", monospace;
  font-size: 1.1rem;
  text-decoration: none;
  text-transform: uppercase;
  border: 1px solid #45fe47;
  color: #45fe47;
  background: transparent;

  &:hover {
    background: #fff;
    color: #000;
    border-color: #fff;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(6px);
`;

const ModalImage = styled.img`
  max-width: min(1100px, 94vw);
  max-height: 88vh;
  border: 1px solid #45fe47;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  border: 0;
  background: transparent;
  color: #45fe47;
  font-family: "VT323", monospace;
  font-size: 2rem;
  cursor: pointer;
`;

const scriptData = [
  {
    id: 1,
    title: "The Farmer and The Thylacine",
    subtitle: "A Journey Through Time in Australia's Heartland",
    logline:
      "A reclusive Tasmanian farmer and his thylacine companion slip through a house that opens onto Australia’s hidden history.",
    description:
      "In a secluded Tasmanian farmhouse, Elias Hart, a reclusive farmer, discovers his home is a portal that transcends time. With his enigmatic companion, Taro, a mysterious Thylacine, Elias embarks on extraordinary journeys through pivotal moments in Australian history—from the prehistoric era and ancient indigenous ceremonies to colonial struggles and modern-day milestones. As temporal landscapes shift around them, Elias uncovers hidden truths, prevents historical tampering, and grapples with the mind-bending wonders he's witnessed. 'The Farmer and The Thylacine' is a captivating fusion of sci-fi and history, highlighting the deep connections between past and present, the wonders of time travel, and the enduring quest for identity.",
    images: [image2, image1],
    genre: "Sci-Fi Tale",
    runtime: "Short Series",
    status: "Concept",
  },
  {
    id: 4,
    title: "The Plain Reckoning",
    subtitle: "The Last Stand of God’s Silent Army",
    logline:
      "When a caliphate targets the Amish, a secret fellowship of believers answers with hymns and the full-auto arsenal of a 3D-printed tomorrow.",
    description:
      "2035. The Brotherhood targets the one community they believe will never fight back.\n\nThey are wrong.\n\nFor fifteen years a hidden fellowship has forged next-generation arms in secret—full-auto weapons born of faith and filament. When the black vehicles finally arrive, the meek do not kneel.\n\nThey rise.",
    images: [image6],
    genre: "Mythic Action",
    runtime: "Feature Film",
    status: "Concept",
  },
  {
    id: 3,
    title: "Island of the Harvester Aliens",
    subtitle: "Tales of the real Bloodsuckers",
    logline:
      "1980s Puerto Rico. Livestock drained of blood. The Chupacabra legend is cover for something worse.",
    description:
      "In the late 1980s, a secluded village in rural Puerto Rico is gripped by terror as livestock are found mutilated, drained of blood. The legend of the Chupacabra spreads fear, but the truth is even more horrifying. An alien species has landed, misinterpreting human behavior and attempting to communicate through violence. As the villagers confront these shape-shifting beings that often appear as innocent children, they must uncover the aliens' intentions and survive the escalating horror.",
    images: [image4, image5],
    genre: "Horror Sci-Fi",
    runtime: "Short Film",
    status: "Concept",
  },
  {
    id: 2,
    title: "The Devil Vacations in PR",
    subtitle: "Future Punk · In development",
    logline:
      "Neon crews, street machines, and island myth under a red-black sky.",
    description: "",
    images: [image3],
    genre: "Future Punk",
    runtime: "Animated Shorts",
    status: "In development",
  },
];

const ImageModal = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <ModalImage
        src={src}
        alt="Concept art"
        onClick={(e) => e.stopPropagation()}
      />
      <ModalClose type="button" onClick={onClose} aria-label="Close">
        [X]
      </ModalClose>
    </ModalOverlay>
  );
};

const FilmScripts = () => {
  const [activeId, setActiveId] = useState(scriptData[0].id);
  const [shotIndex, setShotIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const active = useMemo(
    () => scriptData.find((s) => s.id === activeId) || scriptData[0],
    [activeId]
  );

  const selectFilm = (id) => {
    setActiveId(id);
    setShotIndex(0);
  };

  const openLightbox = (src) => setLightbox(src);

  if (!USE_CINEMA_LAYOUT) {
    return (
      <>
        <HomeButton to="/">Home</HomeButton>
        <Body style={{ paddingTop: "6rem" }}>
          {scriptData.map((film) => (
            <p key={film.id} style={{ color: "#45fe47" }}>
              {film.title}
            </p>
          ))}
        </Body>
      </>
    );
  }

  return (
    <>
      <HomeButton to="/">Home</HomeButton>
      <Page>
        <Hero>
          <HeroArt key={active.id}>
            <img src={active.images[0]} alt="" />
          </HeroArt>
          <HeroContent>
            <NowShowing>Now showing · Concept slate</NowShowing>
            <HeroTitle>{active.title}</HeroTitle>
            {active.subtitle && <HeroSubtitle>{active.subtitle}</HeroSubtitle>}
            <HeroLogline>{active.logline}</HeroLogline>
            <TagRow>
              <Tag>{active.genre}</Tag>
              <Tag>{active.runtime}</Tag>
              <Tag>{active.status}</Tag>
            </TagRow>
            <HeroActions>
              <ActionBtn
                $primary
                type="button"
                onClick={() => openLightbox(active.images[shotIndex] || active.images[0])}
              >
                View art
              </ActionBtn>
              <ActionBtn
                type="button"
                onClick={() => {
                  const el = document.getElementById("film-reel");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Browse slate
              </ActionBtn>
            </HeroActions>
          </HeroContent>
        </Hero>

        <Body>
          <ReelHeader id="film-reel">
            <div>
              <ReelTitle>The slate</ReelTitle>
              <ReelNote>Select a film to stage it above.</ReelNote>
            </div>
          </ReelHeader>

          <Reel>
            {scriptData.map((film) => {
              const selected = film.id === active.id;
              return (
                <PosterCard
                  key={film.id}
                  type="button"
                  $active={selected}
                  onClick={() => selectFilm(film.id)}
                  aria-pressed={selected}
                >
                  {selected && <ActiveMark>Active</ActiveMark>}
                  <PosterArt>
                    <img src={film.images[0]} alt="" />
                  </PosterArt>
                  <PosterCaption>
                    <PosterName>{film.title}</PosterName>
                    <PosterMeta>{film.genre}</PosterMeta>
                  </PosterCaption>
                </PosterCard>
              );
            })}
          </Reel>

          <DetailDock key={`${active.id}-${shotIndex}`}>
            <DetailGallery>
              <DetailMainShot
                type="button"
                onClick={() => openLightbox(active.images[shotIndex])}
                aria-label="Enlarge artwork"
              >
                <img src={active.images[shotIndex]} alt="" />
              </DetailMainShot>
              {active.images.length > 1 && (
                <DetailThumbs>
                  {active.images.map((img, index) => (
                    <DetailThumb
                      key={img}
                      type="button"
                      $active={index === shotIndex}
                      onClick={() => setShotIndex(index)}
                    >
                      <img src={img} alt="" />
                    </DetailThumb>
                  ))}
                </DetailThumbs>
              )}
            </DetailGallery>

            <DetailCopy>
              <DetailKicker>
                {active.genre} · {active.runtime}
              </DetailKicker>
              <DetailTitle>{active.title}</DetailTitle>
              {active.subtitle && (
                <DetailSubtitle>{active.subtitle}</DetailSubtitle>
              )}
              {active.description ? (
                <DetailBody>{active.description}</DetailBody>
              ) : (
                <DetailEmpty>
                  Concept art locked. Synopsis still in the writers’ room.
                </DetailEmpty>
              )}
            </DetailCopy>
          </DetailDock>

          <CtaBand>
            <CtaTitle>Same mind, different medium</CtaTitle>
            <CtaText>
              These worlds sit beside the product work — archetype systems,
              learning tools, and client builds.
            </CtaText>
            <CtaActions>
              <CtaLink $primary to="/templates">
                Browse portfolio
              </CtaLink>
              <CtaExternal
                href="//cal.com/james-studiovoice2fly.com/ai-strategy-session"
                target="_blank"
                rel="noopener noreferrer"
              >
                Book a call
              </CtaExternal>
            </CtaActions>
          </CtaBand>
        </Body>
      </Page>

      <ImageModal src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
};

export default FilmScripts;
